import type {
  PaginationState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import type { TableProps } from '../types';
import { getStoredPageSize, setStoredPageSize } from './use-page-size-state';
import { useTableColumns } from './use-table-columns';
import { useTableData } from './use-table-data';

export const useTable = <T extends object>({
  data = [],
  columns,
  actions,
  searchTerm,
  showNoResults,
  ariaLabel,
  isLoading,
  isNarrow,
  emptyState,
  enableSorting = true,
  enableRowSelection = true,
  rowSelectionDisabledTooltip,
  enableHorizontalScroll = false,
  manualSorting,
  initialSort = [],
  manualPagination,
  overridePageSize,
  hidePagination,
  hidePageSizeDropdown,
  rowCount,
  selectedRows,
  columnVisibility,
  columnPinning = { left: [], right: [] },
  onRowClick,
  onChangeSort,
  onChangePage,
  onChangePageSize,
  onChangeSelectedRows,
  getRowId,
}: TableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    { pageIndex: 0, pageSize: overridePageSize ?? getStoredPageSize() }
  );
  const pagination = manualPagination || internalPagination;

  const tableData = useTableData(data, isLoading, pagination.pageSize);
  const tableColumns = useTableColumns(
    columns,
    actions,
    isLoading,
    selectedRows,
    rowSelectionDisabledTooltip
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: searchTerm ? getFilteredRowModel() : undefined,
    getPaginationRowModel: manualPagination
      ? undefined
      : getPaginationRowModel(),
    enableSorting,
    enableRowSelection,
    manualSorting,
    manualPagination: !!manualPagination,
    rowCount,
    onSortingChange: (updater) => {
      if (onChangeSort) {
        const [columnSort] = getUpdatedValue(updater, sorting);
        onChangeSort(columnSort);
      }
      setSorting(updater);
    },
    onPaginationChange: (updater) => {
      // This is also called when sorting, so we need to check if anything changes
      const { pageIndex, pageSize } = getUpdatedValue(updater, pagination);
      if (pageIndex !== pagination.pageIndex) {
        onChangePage?.(pageIndex);
      }
      if (pageSize !== pagination.pageSize) {
        onChangePageSize?.(pageSize);
        setStoredPageSize(pageSize);
      }
      if (
        !manualPagination &&
        (pageIndex !== internalPagination.pageIndex ||
          pageSize !== internalPagination.pageSize)
      ) {
        setInternalPagination({ pageIndex, pageSize });
      }
    },
    onRowSelectionChange: (updater) => {
      if (onChangeSelectedRows && selectedRows) {
        onChangeSelectedRows(getUpdatedValue(updater, selectedRows));
      }
    },
    getRowId: isLoading ? undefined : getRowId,
    state: {
      sorting,
      pagination,
      rowSelection: selectedRows,
      globalFilter: searchTerm,
      columnVisibility,
      columnPinning,
    },
  });

  const dataLength = data?.length;

  const shouldHidePageSizeDropdown =
    // Hide page size dropdown if hidePageSizeDropdown is true
    hidePageSizeDropdown ||
    // or if overridePageSize is set
    !!overridePageSize ||
    // or if manual pagination and onChangePageSize is not passed in
    (manualPagination && !onChangePageSize) ||
    // or if data length is 10 or less
    (rowCount ?? dataLength) <= 10;

  const shouldShowNoResults =
    (searchTerm && table.getRowCount() === 0) || showNoResults;
  const showEmptyState = !isLoading && dataLength === 0;
  const showPagination = (!isLoading || !!rowCount) && !hidePagination;
  const { pageIndex, pageSize } = pagination;
  const totalItems = table.getRowCount();
  const pageCount = table.getPageCount();
  const currentRowsCount = table.getRowModel().rows.length;

  const handlePageChange = (newPageIndex: number) => {
    if (newPageIndex === pageIndex) {
      return;
    }
    onChangePage?.(newPageIndex);
    if (!manualPagination) {
      setInternalPagination((previous) => ({
        ...previous,
        pageIndex: newPageIndex,
      }));
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === pageSize && pageIndex === 0) {
      return;
    }
    onChangePage?.(0);
    onChangePageSize?.(newPageSize);
    setStoredPageSize(newPageSize);
    if (!manualPagination) {
      setInternalPagination({
        pageIndex: 0,
        pageSize: newPageSize,
      });
    }
  };

  return {
    table,
    isNarrow,
    searchTerm,
    showNoResults: shouldShowNoResults,
    emptyState,
    enableHorizontalScroll,
    ariaLabel,
    hidePageSizeDropdown: shouldHidePageSizeDropdown,
    onRowClick: isLoading ? undefined : onRowClick,
    showEmptyState,
    showPagination,
    pageIndex,
    pageSize,
    totalItems,
    pageCount,
    currentRowsCount,
    onChangePage: handlePageChange,
    onChangePageSize: handlePageSizeChange,
  };
};

// Util for getting the new value from an updater function (or value)
// prettier-ignore
const getUpdatedValue = <T,>(updater: Updater<T>, previousValue: T) =>
  updater instanceof Function ? updater(previousValue) : updater;
